import React, { useState } from 'react';
import { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, TotpMultiFactorGenerator, type TotpSecret } from 'firebase/auth';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { auth } from '../../firebaseConfig';
import { mfaError } from '../common/mfa';
import { mfaStyles as styles } from './mfaStyles';
import { useMfaCaptcha } from './useMfaCaptcha';

export default function MfaEnrollment({ onComplete, onCancel }: { onComplete: () => void; onCancel?: () => void }) {
    const [method, setMethod] = useState<'phone' | 'totp'>('phone');
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [secret, setSecret] = useState<TotpSecret | null>(null);
    const [busy, setBusy] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [resendAt, setResendAt] = useState(0);
    const captcha = useMfaCaptcha(method === 'phone', method);

    const start = async () => {
        setError('');
        if (Date.now() < resendAt && method === 'phone') {
            setError('Wait one minute before requesting another text.');
            return;
        }
        setBusy(true);
        setSending(true);
        setCode('');
        setVerificationId('');
        setSecret(null);
        let captchaUsed = false;
        try {
            const user = auth.currentUser;
            if (!user?.emailVerified)
                throw new Error('Verify your email before setting up MFA.');
            const session = await multiFactor(user).getSession();
            if (method === 'totp')
                setSecret(await TotpMultiFactorGenerator.generateSecret(session));
            else {
                const number = parsePhoneNumberFromString(phone);
                if (!phone.startsWith('+') || !number?.isValid())
                    throw new Error('Enter a valid phone number with its + country calling code.');
                captchaUsed = true;
                setVerificationId(await captcha.verify(verifier => new PhoneAuthProvider(auth).verifyPhoneNumber({ phoneNumber: number.number, session }, verifier)));
                setResendAt(Date.now() + 60_000);
            }
        } catch (reason) {
            setError(mfaError(reason));
        } finally {
            if (captchaUsed)
                captcha.reset();
            setSending(false);
            setBusy(false);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            const user = auth.currentUser;
            if (!user || !/^\d{6}$/.test(code) || !(method === 'totp' ? secret : verificationId))
                throw new Error('Start setup and enter its six-digit verification code.');
            const assertion = method === 'totp'
                ? TotpMultiFactorGenerator.assertionForEnrollment(secret!, code)
                : PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code));
            await multiFactor(user).enroll(assertion, name.trim() || (method === 'phone' ? 'SMS phone' : 'Authenticator app'));
            setSecret(null);
            setCode('');
            onComplete();
        } catch (reason) {
            setError(mfaError(reason));
            setBusy(false);
        }
    };

    return <div className="space-y-4">
        {error && !captcha.error && <p role="alert" className={styles.error}>{error}</p>}
        <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm font-semibold">Security method
                <select className={styles.input} value={method} disabled={busy} onChange={event => {
                    setMethod(event.target.value as 'phone' | 'totp');
                    setSecret(null);
                    setVerificationId('');
                    setCode('');
                    setError('');
                }}><option value="phone">Text message (SMS)</option><option value="totp">Authenticator app</option></select>
            </label>
            <label className="block text-sm font-semibold">Device name (optional)<input className={styles.input} value={name} maxLength={60} disabled={busy} onChange={event => setName(event.target.value)} /></label>
            {method === 'phone' && <><label className="block text-sm font-semibold">Phone number with country calling code
                <input type="tel" autoComplete="tel" className={styles.input} value={phone} disabled={busy || Boolean(verificationId)} onChange={event => setPhone(event.target.value)} required /></label>
                <p className={styles.text}>Texts are available where your mobile network supports delivery. Your number is sent to Google for authentication and abuse prevention. Carrier charges may apply. You can choose an authenticator app instead.</p></>}
            <div id={captcha.id} hidden={method !== 'phone'} />
            {captcha.error && <p role="alert" className={styles.error}>{captcha.error}<button type="button" className="ml-2 underline" disabled={busy} onClick={() => { setError(''); captcha.reset(); }}>Retry security check</button></p>}
            <button type="button" className={styles.secondary} disabled={busy || method === 'phone' && !captcha.ready} onClick={() => void start()}>{method === 'phone' ? verificationId ? 'Send another code' : 'Send verification text' : secret ? 'Start new app setup' : 'Set up authenticator app'}</button>
            {verificationId && <p role="status" className={styles.notice}>Verification text sent. Enter the code below.<button type="button" className="ml-2 underline" onClick={() => { setVerificationId(''); setCode(''); }}>Change number</button></p>}
            {secret && <div className={styles.notice}><p>Add an account in your authenticator app using this setup key:</p><code className="mt-2 block select-all break-all font-mono">{secret.secretKey}</code>
                <p className="mt-2">Keep this key private. Choose time-based codes.</p><a className="mt-2 inline-block font-semibold underline" href={secret.generateQrCodeUrl(auth.currentUser?.email || '', 'IntelOptics')}>Open authenticator app on this device</a></div>}
            {(secret || verificationId) && <label className="block text-sm font-semibold">Six-digit verification code<input className={styles.input} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} disabled={busy} required /></label>}
            <div className="flex gap-3"><button className={styles.button} disabled={busy || !(secret || verificationId)}>{busy ? 'Verifying…' : 'Verify and save method'}</button>
                {onCancel && <button type="button" className={styles.secondary} disabled={busy && !sending} onClick={onCancel}>Cancel</button>}</div>
        </form>
    </div>;
}
